'use strict';

/**
 * Tools for handling html fractions etc.
 */
class FractionsTools {

	constructor() {
		this.fractionsLookup = {
			separator: '&#x2044;', // /
			sup: [
				'&#x2070;', // 0
				'&#x00B9;', // 1
				'&#x00B2;', // 2
				'&#x00B3;', // 3
				'&#x2074;', // 4
				'&#x2075;', // 5
				'&#x2076;', // 6
				'&#x2077;', // 7
				'&#x2078;', // 8
				'&#x2079;' // 9
			],
			sub: [
				'&#x2080;', // 0
				'&#x2081;', // 1
				'&#x2082;', // 2
				'&#x2083;', // 3
				'&#x2084;', // 4
				'&#x2085;', // 5
				'&#x2086;', // 6
				'&#x2087;', // 7
				'&#x2088;', // 8
				'&#x2089;' // 9
			],
			frac: {
				'1/2': '&frac12;',
				'1/3': '&frac13;',
				'1/4': '&frac14;',
				'1/5': '&frac15;',
				'1/6': '&frac16;',
				'1/8': '&frac18;',
				'2/3': '&frac23;',
				'2/5': '&frac25;',
				'3/4': '&frac34;',
				'3/5': '&frac35;',
				'3/8': '&frac38;',
				'4/5': '&frac45;',
				'5/6': '&frac56;',
				'5/8': '&frac58;',
				'7/8': '&frac78;'
			}
		};
	}

	/**
	 * Convert float to a rational number.
	 * (http://jonisalonen.com/2012/converting-decimal-numbers-to-ratios/)
	 *
	 * @param {number} x - Number.
	 * @param {number} tolerance - Tolerance.
	 *
	 * @returns {number[]} Numerator and denominator ([ numerator, dominator ]).
	 */
	float2rat(x, tolerance) {
		let h1 = 1;
		let h2 = 0;
		let k1 = 0;
		let k2 = 1;
		let b = x;

		do {
			let a = Math.floor(b);
			let aux = h1;
			h1 = a * h1 + h2;
			h2 = aux;
			aux = k1;
			k1 = a * k1 + k2;
			k2 = aux;
			b = 1 / (b - a);
		} while (Math.abs(x - h1 / k1) > x * tolerance);

		return [h1, k1];
	}

	/**
	* Convert fraction to decimal
	* Separates fractions from the whole numbers and sums values together
	*
	* @param {string} fraction - Fraction.
	* @returns {(number|string)} Returns original string or number if conversion isn't possible.
	*/
	fractionToDecimal(fraction) {
		if (typeof fraction !== 'string' || !/\d\/\d/.test(fraction)) {
			return fraction;
		}

		const values = fraction.split(' ');
		const sum = [];

		for (const value of values) {

			if (value.includes('/')) {
				const fractionPart = value.split('/');

				const firstPart = String(fractionPart[0]);

				fractionPart[0] = Number(fractionPart[0]);
				fractionPart[1] = Number(fractionPart[1]);

				if (firstPart.length === 1 && fractionPart[0] &&!Number.isNaN(fractionPart[0]) && !Number.isNaN(fractionPart[0])) {
					sum.push(fractionPart[0] / fractionPart[1]);
				} else {
					return fraction;
				}
			} else if (!Number.isNaN(Number(value))) {
				sum.push(Number(value));
			}
		}

		return values.length === sum.length ?
			sum.reduce((a, b) => {
				return a + b;
			}, 0) :
			fraction;
	}

	/**
	 * Converts hex encoded fractions to normal characters.
	 * Example: 1 &#x00B9;&#x2044;&#x2084; -> 1 1/4
	 * 
	 * @param {string} text - String to unfraction.
	 * @param {boolean} [returnDecimalnumber=false] - Return decimal number instead, assuming that the input is in "1 1/4" format.
	 * @returns {(string|number)} Converted value.
	 */
	unfraction(text, returnDecimalnumber) {

		if (typeof text !== 'string') {
			return text;
		}

		// Replace fraction dash
		let tempText = String(text).replace(new RegExp(this.fractionsLookup.separator, 'g'), '/');
		tempText = tempText.replace(new RegExp('&nbsp;', 'g'), ' ');

		for (let i = 0; i < this.fractionsLookup.sup.length; i++) {
			tempText = tempText.replace(new RegExp(this.fractionsLookup.sup[i], 'g'), String(i));
			tempText = tempText.replace(new RegExp(this.fractionsLookup.sub[i], 'g'), String(i));
		}

		for (const key of Object.keys(this.fractionsLookup.frac)) {
			const fracEntity = this.fractionsLookup.frac[key];
			tempText = tempText.replace(new RegExp(fracEntity, 'g'), key);
		}

		if (returnDecimalnumber === true) {
			let [sup, sub] = tempText.trim().split(' ').filter(c => c !== '');
			sup = String(sup).trim();
			sub = String(sub).trim().split('/');
			if (sub.length === 2) {
				sub = sub.reduce((value, frac, i) => {
					if (i === 0) {
						return Number(frac);
					} else {
						return value / Number(frac);
					}
				}, 0);
			} else {
				sub = false;
			}

			if (!Number.isNaN(parseFloat(sup)) && !Number.isNaN(parseFloat(sub))) {
				return Number(sup) + Number(sub);
			}

		}

		return tempText;
	}

	/**
	 * Get unicode hex encoded sup/sub numbers.
	 * 
	 * @param {string} [mode="sup"] - Sup or sub fractions.
	 * @param {(string|number)} number - Integer number.
	 * @returns {string} Encoded sup/sub numbers;
	 */
	getFraction(mode, number) {

		let lookup = this.fractionsLookup.sup;
		if (mode === 'sub') {
			lookup = this.fractionsLookup.sub;
		}

		return Array.from(String(parseInt(number))).map(num => lookup[parseInt(num)]).join('');

	}

	/**
	 * Returns base number and fractions (unicode hex or named entity):
	 * 0.125 (1/8) -> 0 &#x00B9;&#x2044;&#x2088; or 0 &frac18;
	 * 
	 * If you want to convert 25 1/8 to 25 &frac18;, do it like this:
	 * FractionsTools.toFractions(FractionsTools.unfraction("25 1/8", true));
	 *
	 * @param {(string|number)} number - Float to convert.
	 * @param {boolean} [allowNamedEntities=false] - Get named entity i.e. &frac14; if exists.
	 * @returns {string} Returns base and fractions (if possible) or original value.
	 */
	toFractions(number, allowNamedEntities) {

		const tempNumber = parseFloat(String(number).replace(',', '.'));
		if (!Number.isNaN(tempNumber) && tempNumber % 1 !== 0) {
			const fraction = tempNumber % 1;
			const base = tempNumber - fraction;
			const rat = this.float2rat(fraction, 1.0e-2);
			if (allowNamedEntities === true) {
				const frac = this.fractionsLookup.frac[`${rat[0]}/${rat[1]}`];
				if (frac !== undefined) {
					return `${base} ${frac}`;
				}
			}
			return `${base} ${this.getFraction('sup', rat[0])}${this.fractionsLookup.separator}${this.getFraction('sub', rat[1])}`;
		}
		return number;
	}

	/**
	 * Convert to HTML fractions.
	 * 
	 * Example:
	 * Input: 25 1/8 or 25.125
	 * Output: 25 &frac18;
	 * 
	 * @param {(string|number)} value - Value to convert.
	 * @returns {string} - Input converted to fractions.
	 */
	convertToHtmlFractions(value) {
		let _value = '';
		if (typeof value === 'string') {
			_value = this.unfraction(String(value), true);
		} else if (typeof value === 'number') {
			_value = value;
		} else {
			_value = String(value);
		}
		return this.escapeFractionsString(this.toFractions(_value, true) + '');
	}

	/**
	 * Escapes string that contains html escaped entities.
	 * 
	 * @param {string} text - String to process.
	 * @returns {string} Escaped and trimmed value.
	 */
	escapeFractionsString(text) {

		const entityMap = {
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			'\'': '&#39;',
			'/': '&#x2F;',
			'`': '&#x60;',
			'=': '&#x3D;',
			' & ': ' &amp; '
		};

		return String(text).replace(/[<>"'`=/]/g, (s) => {
			return entityMap[s];
		});

		//if (_text.includes("&frac") || _text.includes("&#x2044;")) {
		//	return escapeChars(_text);
		//} else {
		//return _text;
		//}
	}
}

export default new FractionsTools();